import { useMemo, useRef, useState, type ChangeEvent } from "react";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AxiosInstance from "@/utils/AxiosInstance";

const TEMPLATE_FILENAME = "patient_bulk_status_change_template.xlsx";
const TEMPLATE_HEADER = "PATIENT_ID";

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Blocked", value: "blocked" },
  { label: "Archived", value: "archived" },
  { label: "Opt Out (Denied)", value: "denied" },
  { label: "Non Compliant", value: "non_compliant" },
  { label: "Enroll", value: "newly_added" },
] as const;

type BulkStatusValue = (typeof STATUS_OPTIONS)[number]["value"];

type ConsentChecklist = {
  informedConsent: boolean;
};

const DEFAULT_CONSENT: ConsentChecklist = {
  informedConsent: false,
};

function getStatusFieldMode(status: BulkStatusValue | "") {
  if (!status) return "none" as const;
  if (status === "active") return "none" as const;
  if (status === "denied") return "optOutReason" as const;
  if (status === "newly_added") return "enroll" as const;
  return "reason" as const;
}

type PatientBulkStatusChangeProps = {
  onSuccess?: () => void;
  className?: string;
};

export function PatientBulkStatusChange({
  onSuccess,
  className,
}: PatientBulkStatusChangeProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState<BulkStatusValue | "">("");
  const [reason, setReason] = useState("");
  const [optOutReason, setOptOutReason] = useState("");
  const [notes, setNotes] = useState("");
  const [zendeskTicketNumber, setZendeskTicketNumber] = useState("");
  const [consentChecklist, setConsentChecklist] =
    useState<ConsentChecklist>(DEFAULT_CONSENT);

  const fieldMode = getStatusFieldMode(status);

  const canUpload = useMemo(() => {
    if (!status) return false;
    if (fieldMode === "optOutReason") return !!optOutReason.trim();
    if (fieldMode === "reason") return !!reason.trim();
    if (fieldMode === "enroll") {
      return !!notes.trim() && !!zendeskTicketNumber.trim();
    }
    return true;
  }, [
    status,
    fieldMode,
    optOutReason,
    reason,
    notes,
    zendeskTicketNumber,
    consentChecklist,
  ]);

  const resetForm = () => {
    setStatus("");
    setReason("");
    setOptOutReason("");
    setNotes("");
    setZendeskTicketNumber("");
    setConsentChecklist(DEFAULT_CONSENT);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([{ [TEMPLATE_HEADER]: "" }]);
    worksheet["!cols"] = [{ wch: 24 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Patients");
    XLSX.writeFile(workbook, TEMPLATE_FILENAME);
    toast.success("Template downloaded successfully.");
  };

  const toggleConsent = (key: keyof ConsentChecklist) => {
    setConsentChecklist((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!status) {
      toast.error("Please select a status.");
      event.target.value = "";
      return;
    }

    if (!canUpload) {
      toast.error("Please fill all required fields before uploading.");
      event.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("excelFile", file);
    formData.append("status", status);

    if (fieldMode === "optOutReason") {
      formData.append("optOutReason", optOutReason.trim());
    }
    if (fieldMode === "reason") {
      formData.append("reason", reason.trim());
    }
    if (fieldMode === "enroll") {
      formData.append("notes", notes.trim());
      formData.append("zendeskTicketNumber", zendeskTicketNumber.trim());
      formData.append("consentChecklist", JSON.stringify(consentChecklist));
    }

    setUploading(true);
    try {
      const response: any = await AxiosInstance().post(
        "/patients/bulk/status-change",
        formData,
        { isFormData: true, timeout: 5 * 60 * 1000 } as any,
      );
      const result = response?.data?.data || response?.data || {};
      const created = Number(result.updated ?? result.created ?? 0);
      const failed = Number(result.failed ?? 0);
      const message =
        response?.data?.message ||
        `Bulk status change completed: ${created} updated, ${failed} failed`;

      if (failed > 0) toast.info(message);
      else toast.success(message);

      resetForm();
      setOpen(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to change patient status in bulk.",
      );
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={className}
        onClick={() => setOpen(true)}
      >
        <Upload className="mr-2 h-4 w-4" />
        Bulk Status Change
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (uploading) return;
          setOpen(next);
          if (!next) resetForm();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Status Change</DialogTitle>
          </DialogHeader>

          <div className="mt-2 max-h-[70vh] space-y-4 overflow-y-auto pr-1">
            <div className="space-y-2">
              <Label>Status*</Label>
              <Select
                value={status}
                onValueChange={(value) => {
                  setStatus(value as BulkStatusValue);
                  setReason("");
                  setOptOutReason("");
                  setNotes("");
                  setZendeskTicketNumber("");
                  setConsentChecklist(DEFAULT_CONSENT);
                }}
                disabled={uploading}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {fieldMode === "optOutReason" && (
              <div className="space-y-2">
                <Label htmlFor="bulk-opt-out-reason">Opt Out Reason*</Label>
                <Textarea
                  id="bulk-opt-out-reason"
                  placeholder="Enter opt-out reason..."
                  value={optOutReason}
                  onChange={(e) => setOptOutReason(e.target.value)}
                  className="min-h-[90px] resize-none"
                  disabled={uploading}
                />
              </div>
            )}

            {fieldMode === "reason" && (
              <div className="space-y-2">
                <Label htmlFor="bulk-status-reason">Reason*</Label>
                <Textarea
                  id="bulk-status-reason"
                  placeholder="Enter reason..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="min-h-[90px] resize-none"
                  disabled={uploading}
                />
              </div>
            )}

            {fieldMode === "enroll" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bulk-enroll-notes">Enrollment Note*</Label>
                  <Textarea
                    id="bulk-enroll-notes"
                    placeholder="Enrollment note..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-[100px] resize-none"
                    disabled={uploading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bulk-consent-proof">Enter Consent Proof*</Label>
                  <Input
                    id="bulk-consent-proof"
                    placeholder="Consent Proof"
                    value={zendeskTicketNumber}
                    onChange={(e) => setZendeskTicketNumber(e.target.value)}
                    disabled={uploading}
                  />
                </div>

                <div className="space-y-2 rounded-md border p-3">
                  <div className="text-sm text-muted-foreground">
                    Please verify that the patient has been informed verbally of
                    the following information:
                  </div>
                  <div className="space-y-2 pt-1">
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1 accent-blue-500 cursor-pointer"
                        checked={consentChecklist.informedConsent}
                        onChange={() => toggleConsent("informedConsent")}
                        disabled={uploading}
                      />
                      <span>
                        I hereby provide my informed consent to enroll in care
                        management services offered by Anthro Care On behalf of
                        my primary care provider. I understand that these
                        services involve the collection and monitoring of my
                        health data, ongoing care coordination, and
                        communication with healthcare providers to support the
                        management of my condition(s). I acknowledge that
                        participation is voluntary, and I may opt out at any
                        time by informing the care team. I authorize the use and
                        disclosure of my health information as necessary to
                        deliver these services in accordance with applicable
                        laws and regulations.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              className="w-full gap-2"
              onClick={downloadTemplate}
              disabled={uploading || !status}
            >
              <Download className="h-4 w-4" />
              Download Template
            </Button>

            <div className="space-y-2">
              <Label htmlFor="bulk-status-file">Upload File*</Label>
              <Input
                ref={fileInputRef}
                id="bulk-status-file"
                type="file"
                accept=".xlsx,.xls"
                disabled={uploading || !canUpload}
                onChange={handleUpload}
              />
              <p className="text-xs text-muted-foreground">
                Template header must be <span className="font-medium">PATIENT_ID</span>{" "}
                only. Fill required fields above, then upload.
              </p>
            </div>

            {uploading && (
              <p className="text-sm text-muted-foreground">Uploading…</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PatientBulkStatusChange;
